import { BenefitDTOBenefitEnum, CouponGiftDTO, CouponGiftDTOGiftTypeEnum } from 'probonio-shared-ui/api';
import { useCallback, useMemo, useState } from 'react';

import { useTenant } from 'probonio-shared-ui/module/me';
import { useNewDialogState } from '../../../../../component/dialog';
import { DeleteActivationDialog } from '../../../../benefits';
import { useCouponGifts } from '../../../../benefits/coupons/query';
import { GiftsDialog } from '../../../../benefits/gifts';
import { GiftsIncentiveDialog } from '../../../../benefits/gifts/GiftsList/GiftsIncentiveDialog';
import { isBenefitActive } from '../../../../benefits/isBenefitActive';
import { useDeleteGiftActivationMutation } from '../hooks/useDeleteGiftsActivationMutations';
import { useProfileEmployee } from '../hooks/useProfileEmployee';
import GiftActivationActionsContext from './useGiftActivationModal ';

export const GiftActivationModalProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { tenant } = useTenant();
  const { employee } = useProfileEmployee();
  const giftDialog = useNewDialogState();
  const incentiveDialog = useNewDialogState();
  const deleteDialog = useNewDialogState();
  const [giftToEdit, setGiftToEdit] = useState<CouponGiftDTO>();
  const { deleteGift } = useDeleteGiftActivationMutation();

  const { data } = useCouponGifts(employee.id, {});
  const incentives = useMemo(
    () => data?.couponGifts?.filter(gift => gift.giftType === CouponGiftDTOGiftTypeEnum.Incentive),
    [data?.couponGifts],
  );

  const handleOpenIncentiveCreate = useCallback(() => {
    setGiftToEdit(undefined);
    incentiveDialog.handleOpen();
  }, [incentiveDialog]);

  const handleOpenIncentiveEdit = useCallback(
    (gift: CouponGiftDTO) => {
      setGiftToEdit(gift);
      incentiveDialog.handleOpen();
    },
    [incentiveDialog],
  );

  const handleDeleteGift = useCallback(() => {
    deleteGift(giftToEdit!);
    deleteDialog.dialogState.handleClose();
  }, [deleteDialog.dialogState, deleteGift, giftToEdit]);

  const handleOpenGiftCreate = useCallback(() => {
    setGiftToEdit(undefined);
    giftDialog.handleOpen();
  }, [giftDialog]);

  const handleOpenGiftEdit = useCallback(
    (gift: CouponGiftDTO) => {
      setGiftToEdit(gift);
      giftDialog.handleOpen();
    },
    [giftDialog],
  );

  const handleOpenGiftDelete = useCallback(
    (gift: CouponGiftDTO) => {
      setGiftToEdit(gift);
      deleteDialog.handleOpen();
    },
    [deleteDialog],
  );
  return (
    <GiftActivationActionsContext.Provider
      value={{
        onGiftCreate: handleOpenGiftCreate,
        onGiftDelete: handleOpenGiftDelete,
        onGiftEdit: handleOpenGiftEdit,
        onIncentiveCreate: handleOpenIncentiveCreate,
        onIncentiveDelete: handleOpenGiftDelete,
        onIncentiveEdit: handleOpenIncentiveEdit,
      }}
    >
      {children}
      {isBenefitActive(tenant, BenefitDTOBenefitEnum.Gifts) && (
        <GiftsDialog
          dialogState={giftDialog.dialogState}
          employeeId={employee.id}
          currentGift={giftToEdit}
          birthDate={employee?.birthDate}
          showInviteUser={!employee?.user && !employee?.invitationMailSentAt}
        />
      )}
      {isBenefitActive(tenant, BenefitDTOBenefitEnum.Gifts) && (
        <GiftsIncentiveDialog
          dialogState={incentiveDialog.dialogState}
          employeeId={employee.id}
          incentives={incentives ?? []}
          currentGift={giftToEdit}
          showInviteUser={!employee?.user && !employee?.invitationMailSentAt}
        />
      )}

      {giftToEdit && (
        <DeleteActivationDialog
          messageKey="userGiftsPage.deleteConfirmation"
          dialogState={deleteDialog.dialogState}
          onDelete={handleDeleteGift}
        />
      )}
    </GiftActivationActionsContext.Provider>
  );
};
